import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Users, BookOpen, Award, TrendingUp, ArrowLeft, Trophy, GraduationCap, FileCheck } from 'lucide-react';

import { apiFetch } from '../services/api';
const API = process.env.REACT_APP_BACKEND_URL;

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch(`${API}/api/learning/admin/analytics`)
      .then(r => r.json())
      .then(d => { if (d.success) setData(d); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500" />
    </div>
  );

  const o = data?.overview || {};
  const modules = data?.module_stats || [];
  const categories = data?.category_stats || {};
  const topLearners = data?.top_learners || [];

  const catLabels = {
    basis: 'Basis-Schulungen', pv: 'Photovoltaik', smart_home: 'Smart Home',
    buero: 'Büro & Organisation', ki_handwerk: 'KI im Handwerk'
  };

  const catColors = {
    basis: 'text-yellow-500', pv: 'text-orange-500', smart_home: 'text-cyan-500',
    buero: 'text-purple-500', ki_handwerk: 'text-emerald-500'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900">
      <header className="bg-black/50 border-b border-yellow-500/30 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-gray-400 hover:text-white" data-testid="admin-back-btn">
              <ArrowLeft className="w-5 h-5 mr-2" /> Zurück
            </Button>
            <div>
              <h1 className="text-xl font-bold text-yellow-500">Admin Analytics</h1>
              <p className="text-sm text-gray-400">Plattformweite Statistiken</p>
            </div>
          </div>
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30 border">Super-Admin</Badge>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8" data-testid="admin-analytics-page">
        {/* KPI Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Unternehmen', val: o.total_companies, icon: Users, color: 'text-blue-500' },
            { label: 'Mitarbeiter', val: o.total_users, icon: Users, color: 'text-yellow-500' },
            { label: 'Kurs-Einschreibungen', val: o.total_enrollments, icon: BookOpen, color: 'text-cyan-500' },
            { label: 'Abschlüsse', val: o.total_completed, icon: Award, color: 'text-green-500' },
            { label: 'Zertifikate', val: o.total_certificates, icon: FileCheck, color: 'text-purple-500' },
            { label: 'Lektionen absolviert', val: o.total_lessons_completed, icon: GraduationCap, color: 'text-orange-500' },
            { label: 'Abschlussrate', val: `${o.completion_rate || 0}%`, icon: TrendingUp, color: 'text-emerald-500' },
          ].map((kpi, i) => (
            <Card key={i} className="bg-gray-900/50 border-gray-800" data-testid={`kpi-${i}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">{kpi.label}</p>
                    <p className="text-2xl font-bold text-white mt-1">{kpi.val || 0}</p>
                  </div>
                  <kpi.icon className={`h-8 w-8 ${kpi.color} opacity-50`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Category Breakdown */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-yellow-500" /> Kategorien-Übersicht
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {Object.entries(categories).map(([cat, stats]) => (
                <div key={cat} className="bg-black/30 p-4 rounded-lg border border-gray-800">
                  <p className={`font-bold ${catColors[cat] || 'text-white'}`}>{catLabels[cat] || cat}</p>
                  <p className="text-2xl font-bold text-white mt-2">{stats.courses} Kurse</p>
                  <p className="text-sm text-gray-400">{stats.enrolled} eingeschrieben</p>
                  <p className="text-sm text-green-500">{stats.completed} abgeschlossen</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Course Popularity */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-yellow-500" /> Kurs-Beliebtheit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {modules.map((mod, i) => (
                <div key={mod.id} className="flex items-center gap-4 bg-black/30 p-3 rounded-lg" data-testid={`course-stat-${mod.id}`}>
                  <span className="text-gray-500 w-8 text-right font-mono">#{i + 1}</span>
                  <div className="flex-1">
                    <p className="text-white font-medium">{mod.title}</p>
                    <div className="flex gap-4 mt-1">
                      <span className="text-sm text-gray-400">{mod.enrolled} eingeschrieben</span>
                      <span className="text-sm text-green-500">{mod.completed} abgeschlossen</span>
                      <span className="text-sm text-yellow-500">{mod.completion_rate}% Erfolg</span>
                    </div>
                  </div>
                  <div className="w-32 h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-yellow-500 rounded-full transition-all" style={{ width: `${Math.min(mod.completion_rate, 100)}%` }} />
                  </div>
                </div>
              ))}
              {modules.length === 0 && <p className="text-gray-500 text-center py-4">Noch keine Daten verfügbar</p>}
            </div>
          </CardContent>
        </Card>

        {/* Top Learners */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" /> Top-Lerner (SmartVolt-Points)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {topLearners.map((learner, i) => (
                <div key={i} className="flex items-center gap-4 bg-black/30 p-3 rounded-lg">
                  <span className={`text-2xl font-bold ${i === 0 ? 'text-yellow-500' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-amber-700' : 'text-gray-500'}`}>
                    {i + 1}.
                  </span>
                  <div className="flex-1">
                    <p className="text-white font-medium">{learner.name || 'Unbekannt'}</p>
                    <p className="text-sm text-gray-400">{learner.email || ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-yellow-500">{learner.total_points || 0}</p>
                    <p className="text-xs text-gray-400">SmartVolt-Points</p>
                  </div>
                </div>
              ))}
              {topLearners.length === 0 && <p className="text-gray-500 text-center py-4">Noch keine Punkte vergeben</p>}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminAnalytics;
